if (!customElements.get('add-on-offer')) {
  class AddOnOffer extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.status = this.querySelector('[data-addon-status]');

      this.querySelectorAll('[data-addon-card]').forEach((card) => {
        this.initializeCard(card);
      });

      this.querySelector('[data-addon-prev]')?.addEventListener('click', () => this.scrollTrack(-1));
      this.querySelector('[data-addon-next]')?.addEventListener('click', () => this.scrollTrack(1));
    }

    initializeCard(card) {
      const variantControl = card.querySelector('[data-addon-variant]');
      const addButton = card.querySelector('[data-addon-add]');

      variantControl?.addEventListener('change', () => this.updateCard(card));
      addButton?.addEventListener('click', () => this.addVariant(card));

      card.querySelectorAll('[data-addon-dialog-open]').forEach((button) => {
        button.addEventListener('click', () => this.openDialog(card, button.dataset.addonDialogOpen));
      });

      this.updateCard(card);
    }

    selectedData(control) {
      if (!control) return {};
      return control.tagName === 'SELECT'
        ? control.options[control.selectedIndex]?.dataset || {}
        : control.dataset;
    }

    updateCard(card) {
      const control = card.querySelector('[data-addon-variant]');
      const data = this.selectedData(control);
      const image = card.querySelector('.add-on-card__image');
      const addButton = card.querySelector('[data-addon-add]');

      if (card.querySelector('[data-addon-offer-price]')) {
        card.querySelector('[data-addon-offer-price]').textContent = data.offerPrice || '';
      }
      if (card.querySelector('[data-addon-original-price]')) {
        card.querySelector('[data-addon-original-price]').textContent = data.originalPrice || '';
      }
      if (image && data.image && image.src !== data.image) {
        image.src = data.image;
        image.srcset = '';
      }
      if (addButton) addButton.disabled = data.available !== 'true';
    }

    openDialog(card, dialogId) {
      const dialog = document.getElementById(dialogId);
      if (!dialog) return;

      const cardVariant = card.querySelector('[data-addon-variant]');
      const dialogVariant = dialog.querySelector('[data-addon-dialog-variant]');
      if (dialogVariant && cardVariant) dialogVariant.value = cardVariant.value;
      this.updateDialog(dialog);

      if (!dialog.dataset.initialized) {
        dialog.dataset.initialized = 'true';
        dialog.querySelector('[data-addon-dialog-close]')?.addEventListener('click', () => dialog.close());
        dialog.addEventListener('click', (event) => {
          if (event.target === dialog) dialog.close();
        });
        dialog.querySelectorAll('[data-addon-thumb]').forEach((thumb) => {
          thumb.addEventListener('click', () => this.showDialogImage(dialog, thumb));
        });
        dialogVariant?.addEventListener('change', () => {
          cardVariant.value = dialogVariant.value;
          this.updateCard(card);
          this.updateDialog(dialog);
          dialog.close();
        });
      }

      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }
    }

    updateDialog(dialog) {
      const control = dialog.querySelector('[data-addon-dialog-variant]');
      if (!control) return;
      const data = this.selectedData(control);
      const image = dialog.querySelector('.add-on-dialog__image');

      dialog.querySelector('[data-addon-dialog-offer-price]').textContent = data.offerPrice || '';
      dialog.querySelector('[data-addon-dialog-original-price]').textContent = data.originalPrice || '';
      if (image && data.image) {
        image.src = data.image;
        image.srcset = '';
      }
      const details = dialog.querySelector('[data-addon-details]');
      if (details && data.url) details.href = data.url;
    }

    showDialogImage(dialog, thumb) {
      const image = dialog.querySelector('.add-on-dialog__image');
      if (!image || !thumb.dataset.image) return;
      image.src = thumb.dataset.image;
      image.srcset = '';
      image.alt = thumb.dataset.imageAlt || '';
      dialog.querySelectorAll('[data-addon-thumb]').forEach((item) => {
        item.classList.toggle('is-active', item === thumb);
      });
    }

    scrollTrack(direction) {
      const track = this.querySelector('[data-addon-track]');
      const card = track?.querySelector('[data-addon-card]');
      if (!track || !card) return;
      const gap = Number.parseFloat(getComputedStyle(track).columnGap) || 0;
      track.scrollBy({ left: direction * (card.getBoundingClientRect().width + gap), behavior: 'smooth' });
    }

    cartHasDiscountCode(cartData, discountCode) {
      const normalizedCode = discountCode.trim().toLowerCase();
      if (!normalizedCode || !cartData) return false;

      const discountApplications = [
        ...(cartData.cart_level_discount_applications || []),
        ...(cartData.discount_applications || [])
      ];
      const items = Array.isArray(cartData.items) ? cartData.items : [cartData];

      items.forEach((item) => {
        (item?.line_level_discount_allocations || []).forEach((allocation) => {
          if (allocation.discount_application) {
            discountApplications.push(allocation.discount_application);
          }
        });
        (item?.discount_allocations || []).forEach((allocation) => {
          if (allocation.discount_application) {
            discountApplications.push(allocation.discount_application);
          }
        });
      });

      return discountApplications.some((application) => {
        const title = application?.title || application?.code || '';
        return title.trim().toLowerCase() === normalizedCode;
      });
    }

    async addVariant(card) {
      if (this.processing) return;
      const control = card.querySelector('[data-addon-variant]');
      const button = card.querySelector('[data-addon-add]');
      const data = this.selectedData(control);
      if (!control?.value || data.available !== 'true') return;
      let addedSuccessfully = false;

      this.processing = true;
      this.hideStatus();
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.classList.remove('is-success');
      button.classList.add('is-loading');

      try {
        const root = window.Shopify?.routes?.root || '/';
        const cartDrawer = document.querySelector('cart-drawer');
        const sections = cartDrawer?.getSectionsToRender
          ? cartDrawer.getSectionsToRender().map((section) => section.section)
          : ['cart-icon-bubble'];
        const requestBody = {
          items: [{ id: Number(control.value), quantity: 1 }],
          sections,
          sections_url: window.location.pathname
        };
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(requestBody)
        });

        if (!response.ok) throw new Error('Unable to add item');

        const cartData = await response.json();
        const discountCode = (this.dataset.discountCode || '').trim();

        if (discountCode) {
          if (this.cartHasDiscountCode(cartData, discountCode)) {
            await this.showCart(cartDrawer, cartData, button);
            addedSuccessfully = true;
            button.classList.add('is-success');
            return;
          }

          const discountCodes = (this.dataset.existingDiscountCodes || '')
            .split(',')
            .map((code) => code.trim())
            .filter(Boolean);
          if (!discountCodes.some((code) => code.toLowerCase() === discountCode.toLowerCase())) {
            discountCodes.push(discountCode);
          }

          const discountResponse = await fetch(`${root}cart/update.js`, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({
              discount: discountCodes.join(','),
              sections,
              sections_url: window.location.pathname
            })
          });

          if (!discountResponse.ok) {
            await this.showCart(cartDrawer, cartData, button);
            this.showStatus('Item added, but the discount could not be applied. Please refresh the page and try again.');
            return;
          }

          const discountedCartData = await discountResponse.json();
          await this.showCart(cartDrawer, discountedCartData, button);

          if (!this.cartHasDiscountCode(discountedCartData, discountCode)) {
            const message = this.classList.contains('add-on-offer--product')
              ? 'Item added. Add the main product to your cart to activate the discount.'
              : 'Item added, but the discount could not be applied. Please review your cart.';
            this.showStatus(message);
            return;
          }

          addedSuccessfully = true;
          button.classList.add('is-success');
          return;
        }

        await this.showCart(cartDrawer, cartData, button);
        addedSuccessfully = true;
        button.classList.add('is-success');
      } catch (error) {
        this.showStatus('Unable to add this item. Please try again.');
      } finally {
        this.processing = false;
        button.removeAttribute('aria-busy');
        button.disabled = data.available !== 'true';
        if (addedSuccessfully) {
          setTimeout(() => {
            button.classList.remove('is-loading');
            button.classList.remove('is-success');
          }, 1400);
        } else {
          button.classList.remove('is-loading');
          button.classList.remove('is-success');
        }
      }
    }

    async showCart(cartDrawer, cartData, opener) {
      if (cartDrawer && cartData?.sections) {
        await cartDrawer.renderContents(cartData, true);
        return;
      }

      if (cartDrawer) {
        await cartDrawer.refresh(true);
        cartDrawer.open(opener);
        return;
      }

      if (window.location.pathname === (this.dataset.cartUrl || '/cart')) {
        const cartItems = document.querySelector('cart-items');
        if (cartItems) {
          await cartItems.refresh();
          return;
        }
      }

      window.location.assign(this.dataset.cartUrl || `${window.Shopify?.routes?.root || '/'}cart`);
    }

    hideStatus() {
      if (this.status) this.status.hidden = true;
    }

    showStatus(message) {
      if (!this.status) return;
      if (message) this.status.textContent = message;
      this.status.hidden = false;
    }
  }

  customElements.define('add-on-offer', AddOnOffer);
}
